import { DOCUMENT } from "@angular/common";
import { Injectable, inject, signal } from "@angular/core";
import { silentSessionExpired } from "@interceptors/silent-request.token";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/index";

const POLL_MS = 60_000;

/**
 * Flagged conversations not opened since flagging - part of the side menu's
 * Messages dot (main-nav, with NavDotsService).
 * Polled every 60 s while the page is visible; the Messages page calls refresh() after
 * opening a conversation or marking one reviewed so the badge follows at once.
 */
@Injectable({ providedIn: "root" })
export class MessagesUnreadService {
  private readonly requestService = inject(RequestService);
  private readonly document = inject(DOCUMENT);

  readonly count = signal(0);

  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly onVisibility = () => {
    if (!this.document.hidden) this.refresh();
  };

  start(): void {
    if (this.timer) return;
    this.refresh();
    this.timer = setInterval(() => {
      if (!this.document.hidden) this.refresh();
    }, POLL_MS);
    this.document.addEventListener("visibilitychange", this.onVisibility);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.document.removeEventListener("visibilitychange", this.onVisibility);
    this.count.set(0);
  }

  refresh(): void {
    // Session gone (a background poll was refused): stop asking until the next login.
    if (silentSessionExpired()) return;
    this.requestService.get<IApiResponse>("/api/v1/chat/unread", { background: true }).subscribe({
      next: (res) => {
        if (res?.status) this.count.set(Number(res.data?.count) || 0);
      },
      error: () => {},
    });
  }
}
